import React, { useContext, useEffect, useMemo, useRef, useState } from "react"
import { Alert, Button, Col, Container, Input, Modal, ModalBody, ModalFooter, ModalHeader, Row } from "reactstrap"
import { useQuery } from "@tanstack/react-query"

import storage from "services/storage"
import { authUserKey } from "config/API"
import Swal from "sweetalert2"
import { UserContext } from "App"
import "assets/scss/custom/components/periodic-task.scss"
import { Link } from "react-router-dom"
import { CashBalanceMovementModal, CashMovementsHistoryTable } from "components/periodicTasks"
import { Loading } from "components"
import { getLastManualCashCut } from "services/cash-cuts"
import {
  closeCashOperationDay,
  getCashOperationDays,
  getCurrentCashOperationDay,
  openCashOperationDay,
  reopenCashOperationDay,
} from "services/cash-operation-days"
import { getLimitForCashCut } from "services/periodic-tasks"
import { getCashCounterAccount, getCashCounterRegister, getCashMovemnts } from "services/tickets"
import { formatDateParam } from "utils/date-filters"
import {
  formatCurrency as formatMxCurrency,
  getStoredAdjustmentLimit,
} from "utils/cash-movement-analysis"

const ACCOUNT_LABELS = {
  cash_register: "Caja registradora",
  cash_account: "Dinero en Resguardo",
}

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString("es-MX", { style: "currency", currency: "MXN" })

const formatDateTime = (value) => {
  if (!value) {
    return "Sin cortes registrados"
  }

  return new Intl.DateTimeFormat("es-MX", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(value))
}

const formatDateOnly = (value) => {
  if (!value) {
    return "—"
  }

  const isPlainDate = /^\d{4}-\d{2}-\d{2}$/.test(String(value))
  const dt = isPlainDate ? new Date(`${value}T00:00:00`) : new Date(value)

  return new Intl.DateTimeFormat("es-MX", { dateStyle: "long" }).format(dt)
}

const CashRetreat = () => {
  document.title = "Cuenta del día | Punto de venta"
  const user = useContext(UserContext)
  const authUser = storage.get(authUserKey) || {}
  const permissions = storage.get(authUserKey).user_permissions || []
  const canAdd = permissions.some((e) => e.codename === "add_cashcut")
  const userId = user?.id ?? authUser?.id ?? authUser?.user?.id ?? authUser?.user_id ?? authUser?.pk ?? null
  const today = formatDateParam(new Date())

  const [showAlertSuccess, setShowAlertSuccess] = useState(false)
  const [msgSuccess, setMsgSuccess] = useState("")
  const [movementModal, setMovementModal] = useState(null)
  const [openDayModal, setOpenDayModal] = useState(false)
  const [operationDate, setOperationDate] = useState(today)
  const [openingCashRegisterAmount, setOpeningCashRegisterAmount] = useState("")
  const [isOpeningCashOperationDay, setIsOpeningCashOperationDay] = useState(false)
  const [movementsPage, setMovementsPage] = useState(1)
  const operationDateInputRef = useRef(null)
  const openingCashRegisterAmountInitializedRef = useRef(false)

  const cashRegisterQuery = useQuery(["cash-register-balance"], () => getCashCounterRegister(), {
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: 5000,
    staleTime: 0,
  })
  const cashAccountQuery = useQuery(["cash-account-balance"], () => getCashCounterAccount(), {
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: 5000,
    staleTime: 0,
  })
  const cashLimitQuery = useQuery(["cash-limit"], () => getLimitForCashCut(), {
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: 5000,
    staleTime: 0,
  })
  const cashOperationDayQuery = useQuery(["cash-operation-day-current"], () => getCurrentCashOperationDay(), {
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: 5000,
    staleTime: 0,
  })
  const currentCashOperationDay = cashOperationDayQuery.data || null
  const cashOperationDaysQuery = useQuery(
    ["cash-operation-days-latest"],
    () =>
      getCashOperationDays({
        ordering: "-opened_at",
        per_page: 1,
        offset: 0,
      }),
    {
      refetchOnMount: "always",
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchInterval: 5000,
      staleTime: 0,
    }
  )
  const lastCashOperationDay = cashOperationDaysQuery.data?.results?.[0] || null
  const selectedCashOperationDayQuery = useQuery(
    ["cash-operation-days-selected", operationDate],
    () =>
      getCashOperationDays({
        operation_date: operationDate,
        per_page: 1,
        offset: 0,
      }),
    {
      enabled: openDayModal && Boolean(operationDate),
      refetchOnMount: "always",
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      staleTime: 0,
    }
  )
  const selectedCashOperationDay = selectedCashOperationDayQuery.data?.results?.[0] || null
  const isSelectedCashOperationDayClosed = Boolean(selectedCashOperationDay?.closed_at)
  const displayCashOperationDay = currentCashOperationDay || lastCashOperationDay
  const displayCashOperationDayLabel = currentCashOperationDay ? "Día abierto actual" : "Último día abierto"
  const displayCashOperationDayStatus = currentCashOperationDay
    ? "Abierto"
    : lastCashOperationDay
    ? "Cerrado"
    : "Sin registros"
  const currentCashOperationDayDate = currentCashOperationDay?.operation_date || null
  const cashMovementsFilter = useMemo(() => {
    if (!currentCashOperationDayDate) {
      return null
    }

    return {
      date__gte: currentCashOperationDayDate,
      date__lte: currentCashOperationDayDate,
    }
  }, [currentCashOperationDayDate])
  const lastManualCashCutQuery = useQuery(["last-manual-cash-cut"], () => getLastManualCashCut(), {
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: 5000,
    staleTime: 0,
  })
  const cashMovementsQuery = useQuery(
    ["cash-movements-history", movementsPage, currentCashOperationDayDate || "closed"],
    () =>
      cashMovementsFilter
        ? getCashMovemnts({
            ordering: "-created_at",
            per_page: 30,
            offset: (movementsPage - 1) * 30,
            movement_type: "withdraw",
            ...cashMovementsFilter,
          })
        : Promise.resolve({ results: [], count: 0 }),
    {
      refetchOnMount: "always",
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchInterval: 5000,
      staleTime: 0,
    }
  )
  const adjustmentMovementsQuery = useQuery(
    ["cash-movements-adjustment-limit"],
    () =>
      getCashMovemnts({
        ordering: "created_at",
        per_page: 200,
        offset: 0,
        movement_type: "adjustment",
      }),
    {
      refetchOnMount: "always",
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchInterval: 5000,
      staleTime: 0,
    }
  )
  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    didOpen: (toast) => {
      toast.addEventListener("mouseenter", Swal.stopTimer)
      toast.addEventListener("mouseleave", Swal.resumeTimer)
    },
  })

  useEffect(() => {
    if (showAlertSuccess) {
      Toast.fire("¡Actualizado!", msgSuccess, "success")
      setShowAlertSuccess(false)
    }
  }, [showAlertSuccess])

  useEffect(() => {
    if (!openDayModal) {
      setOperationDate(today)
      setOpeningCashRegisterAmount("")
      openingCashRegisterAmountInitializedRef.current = false
    }
  }, [openDayModal, today])

  const refetchBalances = async () => {
    await Promise.all([cashRegisterQuery.refetch(), cashAccountQuery.refetch()])
  }

  const refetchCashOperationDay = async () => {
    await Promise.all([cashOperationDayQuery.refetch(), cashOperationDaysQuery.refetch()])
  }

  const openMovementModal = (config) => {
    setMovementModal(config)
  }

  const closeMovementModal = () => {
    setMovementModal(null)
  }

  const isCashOperationDayOpen = Boolean(currentCashOperationDay)
  const canOperateCashFlow = isCashOperationDayOpen

  const cashRegister = cashRegisterQuery.data || { cash: 0 }
  const cashAccount = cashAccountQuery.data || { cash: 0 }
  const currentCashRegisterAmount = Number(cashRegister.cash || 0)
  const requestedOpeningCashRegisterAmount = Number(openingCashRegisterAmount || 0)
  const openingCashRegisterAdjustment = requestedOpeningCashRegisterAmount - currentCashRegisterAmount
  const isOpeningCashRegisterAmountValid = openingCashRegisterAmount !== "" && requestedOpeningCashRegisterAmount >= 0
  const adjustmentLimit = getStoredAdjustmentLimit()
  const openingAdjustmentAmountLimit = Math.abs(openingCashRegisterAdjustment)
  const isOpeningAdjustmentOutOfLimit = openingAdjustmentAmountLimit > adjustmentLimit
  const dailySalesSummary = currentCashOperationDay?.sales_summary || {
    cash_sales_amount: 0,
    card_sales_amount: 0,
    transfer_sales_amount: 0,
    total_sales_amount: 0,
    receivable_amount: 0,
  }
  const dailyCashSales = Number(dailySalesSummary.cash_sales_amount || 0)
  const dailyCardSales = Number(dailySalesSummary.card_sales_amount || 0)
  const dailyTransferSales = Number(dailySalesSummary.transfer_sales_amount || 0)
  const dailySalesTotal = Number(dailySalesSummary.total_sales_amount || 0)
  const dailyReceivableTotal = Number(dailySalesSummary.receivable_amount || 0)
  const cashLimit = Number(cashLimitQuery.data?.results?.[0]?.limit || 0)
  const cashRegisterBalance = currentCashRegisterAmount
  const cashRegisterRemaining = cashLimit - cashRegisterBalance
  const totalCash = Number(cashRegister.cash || 0) + Number(cashAccount.cash || 0)
  const cashOperationDaySnapshot = {
    cashSales: Number(dailyCashSales || 0),
    cardSales: Number(dailyCardSales || 0),
    transferSales: Number(dailyTransferSales || 0),
    receivableSales: Number(dailyReceivableTotal || 0),
    cashRegister: Number(cashRegister.cash || 0),
    securedCash: Number(cashAccount.cash || 0),
  }

  useEffect(() => {
    if (openDayModal && !openingCashRegisterAmountInitializedRef.current) {
      setOpeningCashRegisterAmount(String(currentCashRegisterAmount.toFixed(2)))
      openingCashRegisterAmountInitializedRef.current = true
    }
  }, [currentCashRegisterAmount, openDayModal])

  const handleOpenCashOperationDay = async () => {
    if (isOpeningCashOperationDay) {
      return
    }

    if (isSelectedCashOperationDayClosed) {
      const result = await Swal.fire({
        title: "Reabrir día cerrado",
        html: `
          <div class="text-start">
            <p class="mb-2">
              El día seleccionado ya está cerrado. Si continúas, se volverá a activar ese mismo día para
              registrar ventas y movimientos.
            </p>
            <p class="mb-0">
              <strong>Día que se reabrirá:</strong> ${formatDateOnly(operationDate)}
            </p>
          </div>
        `,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Reabrir día",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#61a725",
      })

      if (!result.isConfirmed) {
        return
      }

      setIsOpeningCashOperationDay(true)

      try {
        await reopenCashOperationDay({ operation_date: operationDate })
        await Promise.all([refetchCashOperationDay(), refetchBalances()])
        setOpenDayModal(false)
        Toast.fire("Actualizado", "El día de operación quedó reabierto.", "success")
      } catch (error) {
        const errorMessage = error?.errors?.map((item) => item?.message).filter(Boolean).join(" ") ||
          error?.message || "No se pudo reabrir el día de operación."

        await Swal.fire({
          title: "No se puede reabrir el día",
          text: errorMessage,
          icon: "error",
          confirmButtonText: "Entendido",
        })
      } finally {
        setIsOpeningCashOperationDay(false)
      }

      return
    }

    setIsOpeningCashOperationDay(true)

    try {
      await openCashOperationDay({
        operation_date: operationDate,
        cash_register_amount: requestedOpeningCashRegisterAmount,
      })
      await Promise.all([refetchCashOperationDay(), refetchBalances()])
      setOpenDayModal(false)
      Toast.fire("¡Actualizado!", "El día de operación quedó abierto.", "success")
    } catch (error) {
      const errorMessage = error?.errors?.map((item) => item?.message).filter(Boolean).join(" ") ||
        error?.message || "No se pudo abrir el día de operación."

      await Swal.fire({
        title: "No se puede abrir el día",
        text: errorMessage,
        icon: "error",
        confirmButtonText: "Entendido",
      })
    } finally {
      setIsOpeningCashOperationDay(false)
    }
  }

  const openOperationDatePicker = () => {
    const input = operationDateInputRef.current
    if (!input) return

    if (typeof input.showPicker === "function") {
      try {
        input.showPicker()
        return
      } catch (error) {
        input.focus()
        return
      }
    }

    input.focus()
  }

  const handleCloseCashOperationDay = async () => {
    const result = await Swal.fire({
      title: "Cerrar día",
      html: `
        <div class="text-start">
          <p class="mb-2">Se guardarán los datos actuales.</p>
          <ul class="mb-0 ps-3">
            <li><strong>Ventas en efectivo:</strong> ${formatCurrency(cashOperationDaySnapshot.cashSales)}</li>
            <li><strong>Ventas con tarjeta:</strong> ${formatCurrency(cashOperationDaySnapshot.cardSales)}</li>
            <li><strong>Ventas con transferencia:</strong> 
            ${formatCurrency(cashOperationDaySnapshot.transferSales)}</li>
            <li><strong>Pagares pendientes:</strong> ${formatCurrency(cashOperationDaySnapshot.receivableSales)}</li>
            <li><strong>Caja registradora:</strong> ${formatCurrency(cashOperationDaySnapshot.cashRegister)}</li>
            <li><strong>Dinero en resguardo:</strong> ${formatCurrency(cashOperationDaySnapshot.securedCash)}</li>
          </ul>
          <p class="mt-3 mb-0">El acumulado de ventas no se reiniciará. Solo se cerrará el día.</p>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Cerrar día",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#d33",
    })

    if (!result.isConfirmed) {
      return
    }

    try {
      await closeCashOperationDay()
      await Promise.all([refetchCashOperationDay(), refetchBalances()])
      Toast.fire("¡Actualizado!", "El día de operación quedó cerrado.", "success")
    } catch (error) {
      return error
    }
  }

  const renderAccountCard = ({
    title,
    subtitle,
    icon,
    balance,
    actions,
    footer,
    details,
    showBalance = true,
    detailsClassName = "",
  }) => (
    <Col md="4" lg="4" className="mb-3">
      <div className="bg-white p-4 h-100 rounded shadow-sm border d-flex flex-column">
        <div className="d-flex justify-content-between align-items-start gap-3">
          <div>
            <h3 className="mb-2">{title}</h3>
            <p className="text-muted mb-0" style={{ minHeight: "2.5rem" }}>
              {subtitle}
            </p>
          </div>
          <div className="display-grid col-icon" style={{ fontSize: "2rem" }}>
            <i className={icon} />
          </div>
        </div>

        <div className={showBalance ? "mt-4" : "mt-3"}>
          {showBalance && (
            <>
              <div className="text-muted small">Saldo actual</div>
              <div className="display-6 fw-bold">{formatCurrency(balance)}</div>
            </>
          )}
          {details && <div className={`mt-3 ${detailsClassName}`}>{details}</div>}
        </div>

        <div className="mt-auto pt-4">
          {footer && <div className="text-muted fs-6 mb-3">{footer}</div>}

          {canOperateCashFlow && actions.length > 0 && (
            <div className="d-flex flex-wrap gap-2">
            {actions.map((action) => (
              <Button key={action.label} color={action.color} onClick={() => openMovementModal(action)}>
                <i className={`me-2 fa ${action.icon}`} />
                {action.label}
              </Button>
            ))}
            </div>
          )}
        </div>
      </div>
    </Col>
  )

  return !permissions.some((e) => e.codename === "view_cashcut") ? (
    <div className="page-content">
      <div className="text-center">
        <h1>No autorizado para ver esta pagina</h1>
      </div>
    </div>
  ) : (
    <div className="page-content" style={{ paddingTop: "1rem" }}>
      <Container fluid>
        {(cashRegisterQuery.isError || cashAccountQuery.isError || 
        cashLimitQuery.isError || lastManualCashCutQuery.isError || cashMovementsQuery.isError || 
        cashOperationDayQuery.isError) && (
          <Alert color="danger">
            {[cashRegisterQuery.error, cashAccountQuery.error, cashLimitQuery.error]
              .concat(lastManualCashCutQuery.error)
              .concat(cashOperationDayQuery.error)
              .concat(cashMovementsQuery.error)
              .filter(Boolean)
              .flatMap((item) => item?.errors?.map((error) => error?.message) || [])
              .join(" ")}
          </Alert>
        )}

        <Row className="align-items-end mb-2">
          <Col>
            <h2 className="mb-1">Control de efectivo</h2>
            <p className="text-muted mb-0">Gestiona el efectivo disponible en caja.</p>
          </Col>
          <Col md="auto">
            <div className="d-flex flex-column flex-md-row gap-2 align-items-md-center">
              <Button
                tag={Link}
                to="/cash-movements-history"
                color="info"
                className="d-inline-flex align-items-center"
              >
                <i className="me-2 fa fa-history" />
                Ver movimientos
              </Button>
              {canAdd && (
                <Button
                  color={isCashOperationDayOpen ? "danger" : "success"}
                  className="d-inline-flex align-items-center"
                  onClick={isCashOperationDayOpen ? handleCloseCashOperationDay : () => setOpenDayModal(true)}
                >
                  <i className={`me-2 fa ${isCashOperationDayOpen ? "fa-lock" : "fa-unlock"}`} />
                  {isCashOperationDayOpen ? "Cerrar día" : "Abrir día"}
                </Button>
              )}
              <div className="bg-white px-3 py-2 rounded border">
                <div className="text-muted small">Total disponible</div>
                <strong>{formatCurrency(totalCash)}</strong>
              </div>
            </div>
          </Col>
        </Row>

        <div className="text-muted mb-3">
          <strong className="text-dark">{displayCashOperationDayLabel}:</strong>{" "}
          {displayCashOperationDay ? (
            <span>
              {formatDateOnly(displayCashOperationDay.operation_date)} · 
              <strong> Estado: </strong> {displayCashOperationDayStatus}
              <br />
              {displayCashOperationDay.opened_at ? 
              ` · Abierto el ${formatDateTime(displayCashOperationDay.opened_at)}` : ""}
              <br />
              {displayCashOperationDay.closed_at ? 
              ` · Cerrado el ${formatDateTime(displayCashOperationDay.closed_at)}` : ""}
            </span>
          ) : (
            <span>No hay días de operación registrados todavía.</span>
          )}
        </div>

        {cashRegisterQuery.isLoading || cashAccountQuery.isLoading || 
        cashLimitQuery.isLoading || lastManualCashCutQuery.isLoading || cashOperationDayQuery.isLoading || 
        cashOperationDaysQuery.isLoading ? (
          <div className="text-center py-3">
            <Loading isLoading size={2} />
          </div>
        ) : (
          <>
            {!isCashOperationDayOpen && (
              <Alert color="warning" className="d-flex flex-column flex-md-row 
              justify-content-between align-items-md-center gap-3">
                <div>
                  <strong>No puedes registrar ventas ni movimientos hasta abrir el día.</strong> 
                </div>
              </Alert>
            )}

            <Row>
            {renderAccountCard({
              title: "Ventas del día",
              subtitle: "Ventas del día por método de pago.",
              balance: dailySalesTotal,
              actions: [],
              showBalance: false,
              detailsClassName: "fs-5",
              details: (
                <div className="d-flex flex-column gap-2 p-3 rounded">
                  <div className="d-flex justify-content-between gap-3">
                    <span>Efectivo</span>
                    <strong className="fs-3">{formatCurrency(dailyCashSales)}</strong>
                  </div>
                  <div className="d-flex justify-content-between gap-3">
                    <span>Tarjeta</span>
                    <strong className="fs-3">{formatCurrency(dailyCardSales)}</strong>
                  </div>
                  <div className="d-flex justify-content-between gap-3">
                    <span>Transferencia</span>
                    <strong className="fs-3">{formatCurrency(dailyTransferSales)}</strong>
                  </div>
                  <div className="d-flex justify-content-between gap-3 pt-2 border-top fw-bold fs-4">
                    <span>Total</span>
                    <strong className="fs-2">{formatCurrency(dailySalesTotal)}</strong>
                  </div>
                  <div className="d-flex justify-content-between gap-3 mt-5">
                    <span>Pagares pendientes</span>
                    <strong className="fs-3">{formatCurrency(dailyReceivableTotal)}</strong>
                  </div>
                </div>
              ),
              // footer: `Último corte manual: ${formatDateTime(lastManualCashCut?.date)}`,
            })}

            {renderAccountCard({
              title: ACCOUNT_LABELS.cash_register,
              subtitle: "Dinero de ventas en efectivo y cambio.",
              icon: "fa fa-money",
              balance: cashRegister.cash,
              actions: [
                {
                  label: "Mover a Dinero en Resguardo",
                  color: "warning",
                  icon: "fa-exchange-alt",
                  movementType: "transfer",
                  sourceAccountType: "cash_register",
                  destinationAccountType: "cash_account",
                  requiresReason: false,
                  title: "Mover efectivo a Dinero en Resguardo",
                  accountLabel: ACCOUNT_LABELS.cash_register,
                },
              ],
              footer: cashLimit > 0 ? (
                <Alert
                  color={cashRegisterBalance >= cashLimit ? "danger" : cashRegisterRemaining <= 2000 ?
                    "warning" : "info"}
                  className="mb-0 py-2"
                >
                  <strong>Límite de efectivo:</strong> {formatCurrency(cashLimit)}
                  {cashRegisterBalance >= cashLimit ? (
                    <div>El efectivo en caja llegó al límite. Retira o mueve el efectivo para continuar cobrando.</div>
                  ) : cashRegisterRemaining <= 2000 ? (
                    <div>Te quedan {formatCurrency(cashRegisterRemaining)} antes de llegar al límite.</div>
                  ) : (
                    <div>Restan {formatCurrency(cashRegisterRemaining)} antes de llegar al límite.</div>
                  )}
                </Alert>
              ) : null,
            })}

            {renderAccountCard({
              title: ACCOUNT_LABELS.cash_account,
              subtitle: "Efectivo guardado fuera de la caja registradora.",
              icon: "fa fa-bank",
              balance: cashAccount.cash,
              actions: [
                {
                  label: "Agregar efectivo",
                  color: "success",
                  icon: "fa-plus",
                  movementType: "add",
                  destinationAccountType: "cash_account",
                  requiresReason: false,
                  title: "Agregar efectivo a Dinero en Resguardo",
                  accountLabel: ACCOUNT_LABELS.cash_account,
                },
                {
                  label: "Retirar efectivo",
                  color: "danger",
                  icon: "fa-minus",
                  movementType: "withdraw",
                  sourceAccountType: "cash_account",
                  requiresReason: true,
                  title: "Retirar efectivo de Dinero en Resguardo",
                  accountLabel: ACCOUNT_LABELS.cash_account,
                },
              ],
            })}

            <CashMovementsHistoryTable
              movements={cashMovementsQuery.data?.results || []}
              isLoading={cashMovementsQuery.isLoading}
              isError={cashMovementsQuery.isError}
              error={cashMovementsQuery.error}
              currentCashOperationDay={currentCashOperationDay}
              title="Retiros del día"
              description="Retiros desde Dinero en Resguardo."
              showFilters={false}
              showDateColumn={false}
              showMovementTypeColumn={false}
              showAccountColumn={false}
              showActionsColumn={false}
              defaultTypeFilter="withdraw"
              currentPage={movementsPage}
              totalCount={cashMovementsQuery.data?.count || 0}
              pageSize={30}
              onChangePage={(p) => setMovementsPage(p)}
            />
            </Row>
          </>
        )}
      </Container>

      <Modal
        isOpen={openDayModal}
        toggle={() => {
          if (!isOpeningCashOperationDay) {
            setOpenDayModal(false)
          }
        }}
        centered
        backdrop="static"
        keyboard={!isOpeningCashOperationDay}
        modalClassName="cash-open-day-modal"
      >
        <ModalHeader toggle={() => {
          if (!isOpeningCashOperationDay) {
            setOpenDayModal(false)
          }
        }}>Abrir día</ModalHeader>
        <ModalBody>
          {isOpeningCashOperationDay ? (
            <div className="text-center py-5">
              <Loading isLoading size={2} />
              <div className="mt-3 fw-semibold">Procesando apertura de caja...</div>
            </div>
          ) : (
            <>
          <Row className="mb-3">
            <Col md="6">
              <label className="form-label">Selecciona el día a abrir</label>
              <Input
                innerRef={operationDateInputRef}
                type="date"
                value={operationDate}
                onClick={openOperationDatePicker}
                onFocus={openOperationDatePicker}
                onChange={(event) => setOperationDate(event.target.value)}
              />
            </Col>
          </Row>
          <div className="text-start">
            <p className="mb-2">Se abrirá el día con los datos actuales.</p>
            <ul className="mb-0 ps-3">
              <li>
                <strong>Ventas en efectivo:</strong> {formatCurrency(cashOperationDaySnapshot.cashSales)}
              </li>
              <li>
                <strong>Ventas con tarjeta:</strong> {formatCurrency(cashOperationDaySnapshot.cardSales)}
              </li>
              <li>
                <strong>Ventas con transferencia:</strong> {formatCurrency(cashOperationDaySnapshot.transferSales)}
              </li>
              <li>
                <strong>Pagares pendientes:</strong> {formatCurrency(cashOperationDaySnapshot.receivableSales)}
              </li>
              <li>
                <strong>Caja registradora:</strong>{" "}
                {isSelectedCashOperationDayClosed ? (
                  formatCurrency(cashOperationDaySnapshot.cashRegister)
                ) : (
                  <span
                    className="d-inline-flex align-items-center"
                    style={{ maxWidth: "150px", verticalAlign: "middle" }}
                  >
                    <Input
                      bsSize="sm"
                      type="number"
                      min="0"
                      step="0.01"
                      value={openingCashRegisterAmount}
                      onChange={(event) => setOpeningCashRegisterAmount(event.target.value)}
                      invalid={!isOpeningCashRegisterAmountValid}
                      className="py-0"
                    />
                  </span>
                )}
              </li>
              {!isSelectedCashOperationDayClosed && (
                <li>
                  <strong>Ajuste de apertura:</strong> {openingCashRegisterAdjustment >= 0 ? "+" : ""}
                  {formatCurrency(openingCashRegisterAdjustment)}
                </li>
              )}
              <li>
                <strong>Dinero en resguardo:</strong> {formatCurrency(cashOperationDaySnapshot.securedCash)}
              </li>
            </ul>
            <p className="mt-3 mb-0">El acumulado de ventas no se reiniciará. Solo se abrirá el día.</p>
            {!isSelectedCashOperationDayClosed && isOpeningAdjustmentOutOfLimit && (
              <Alert color="danger" className="mt-3 mb-0">
                El ajuste de apertura es de {formatMxCurrency(openingCashRegisterAdjustment)}.
                Limite actual: +/- {formatMxCurrency(adjustmentLimit)}. No se puede abrir el dia con ese ajuste.
              </Alert>
            )}
          </div>
            </>
          )}
        </ModalBody>
        {!isOpeningCashOperationDay && (
          <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setOpenDayModal(false)}
            disabled={isOpeningCashOperationDay}
          >
            Cancelar
          </Button>
          <Button
            color={isSelectedCashOperationDayClosed ? "warning" : "success"}
            onClick={handleOpenCashOperationDay}
            disabled={
              isOpeningCashOperationDay ||
              !operationDate ||
              selectedCashOperationDayQuery.isFetching ||
              adjustmentMovementsQuery.isFetching ||
              (!isSelectedCashOperationDayClosed &&
                (!isOpeningCashRegisterAmountValid || isOpeningAdjustmentOutOfLimit))
            }
          >
            <i className={`me-2 fa ${isSelectedCashOperationDayClosed ? "fa-lock-open" : "fa-unlock"}`} />
            {isSelectedCashOperationDayClosed ? "Reabrir día" : "Abrir día"}
          </Button>
          </ModalFooter>
        )}
      </Modal>

      {movementModal && (
        <CashBalanceMovementModal
          isOpen={Boolean(movementModal)}
          toggle={closeMovementModal}
          title={movementModal.title}
          accountLabel={movementModal.accountLabel}
          userId={userId}
          movementType={movementModal.movementType}
          sourceAccountType={movementModal.sourceAccountType}
          destinationAccountType={movementModal.destinationAccountType}
          requiresReason={movementModal.requiresReason}
          setShowAlertSuccess={setShowAlertSuccess}
          setMsgSuccess={setMsgSuccess}
          refetchBalances={refetchBalances}
        />
      )}
    </div>
  )
}

export default CashRetreat
